import React, { useEffect, useMemo, useState } from "react";
import { Search, X, Folder, ChevronRight, ArrowLeft } from "lucide-react";
import Backend from "../../../../Backend/Backend";
import toast from "react-hot-toast";
import useSize from "../../../../Utils/useSize/useSize";
import Loading from "../../../../Utils/Loading/Loading";
import NotFound from "../../../../Utils/NotFound/NotFound";
import SymbolCard from "./SymbolCard";

export default function AddSymbol() {
  const windowSize = useSize();

  const [backend, setBackend] = useState(null);
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Init backend + load data
  useEffect(() => {
    initBackend();
  }, []);

  const initBackend = async () => {
    try {
      const b = await new Backend().init();
      setBackend(b);

      // Load cached categories
      const cached = localStorage.getItem("categories");
      if (cached) {
        setCategories(JSON.parse(cached));
        setLoading(false);
      }

      loadSymbols(b);
    } catch (err) {
      console.error(err);
      toast.error("Backend init failed");
    }
  };

  const loadSymbols = async (b = backend) => {
    if (!b) return;
    try {
      const data = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        user_id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        watchlist_id: "1",
      };

      const r = await b.all_symbol(data);

      if (r.error === "False") {
        const newData = r.categories;
        const cached = localStorage.getItem("categories");

        if (!cached || JSON.stringify(JSON.parse(cached)) !== JSON.stringify(newData)) {
          setCategories(newData);
          localStorage.setItem("categories", JSON.stringify(newData));
        }
      } else {
        if (r.logout === 1) {
          window.LogOut();
        } else {
          toast.error(r.message);
        }
      }
    } catch (err) {
      setLoading(false);
      toast.error("Failed to load symbols");
    }
  };

  // Memoized all symbols list (fast search)
  const allSymbols = useMemo(() => {
    return categories.flatMap((cat) =>
      cat.symbols.map((sym) => ({
        ...sym,
        category: cat.name,
      }))
    );
  }, [categories]);

  // Search logic
  useEffect(() => {
    if (search.trim() === "") {
      setSearchResults([]);
      return;
    }

    const filtered = allSymbols.filter(
      (s) =>
        s.symbol_display?.toLowerCase().includes(search.toLowerCase()) ||
        s.name?.toLowerCase().includes(search.toLowerCase())
    );

    setSearchResults(filtered);
  }, [search, allSymbols]);

  return (
    <div
      style={{ height: windowSize[1] - 10 }}
      className="flex flex-col bg-[var(--background)]"
    >
      {/* Search Bar */}
      <div className="px-4 pt-4 flex items-center gap-2">
        {selectedCategory && (
          <button
            onClick={() => setSelectedCategory(null)}
            className="p-2 rounded-full hover:bg-[var(--hover)] transition"
          >
            <ArrowLeft className="w-5 h-5 text-[var(--text)]" />
          </button>
        )}

        <div className="relative flex items-center flex-1 rounded-2xl shadow-sm">
          <div className="absolute left-3">
            <Search className="w-5 h-5 text-[var(--text)]" />
          </div>

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search symbols or markets..."
            className="w-full font-semibold pl-10 pr-12 py-2.5 bg-[var(--background)] text-[15px] text-[var(--text)] placeholder-[var(--subtext)] outline-none border-2 border-[var(--hover)] rounded-2xl"
          />

          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 p-1.5 hover:bg-gray-200 rounded-full transition"
            >
              <X className="w-4 h-4 text-[var(--text)]" />
            </button>
          )}
        </div>
      </div>

      {/* List Container */}
      <div
        style={{
          height: windowSize[1] - 80,
          overflowY: "auto",
          scrollBehavior: "smooth",
          paddingBottom: 200,
        }}
        className="flex-1 mt-4 px-2"
      >
        {loading ? (
          <Loading height={windowSize[1] - 80} />
        ) : search.trim() !== "" ? (
          searchResults.length > 0 ? (
            searchResults.map((s, i) => (
              <SymbolCard s={s} i={i} key={s.symbol_id || i} />
            ))
          ) : (
            <NotFound
              small
              height={windowSize[1] - 80}
              title="No results found"
              subtext="Try a different symbol or keyword."
            />
          )
        ) : selectedCategory ? (
          selectedCategory.symbols.length > 0 ? (
            selectedCategory.symbols.map((s, i) => (
              <SymbolCard s={s} i={i} key={s.symbol_id || i} />
            ))
          ) : (
            <NotFound
              small
              height={windowSize[1] - 80}
              title="No symbols found"
              subtext="This category has no items."
            />
          )
        ) : categories.length > 0 ? (
          categories.map((item, idx) => (
            <div
              key={idx}
              onClick={() => setSelectedCategory(item)}
              className="flex mx-2 justify-between items-center py-3 border-b border-[var(--hover)] px-2 transition cursor-pointer hover:bg-[var(--hover)] rounded-xl"
            >
              <div className="flex items-center gap-3">
                <Folder className="w-5 h-5 text-yellow-500" />
                <span className="text-[15px] font-semibold text-[var(--text)]">
                  {item.name}
                </span>
              </div>
              <div className="flex items-center font-semibold text-[var(--text)]">
                <span>
                  {item.selected} / {item.total}
                </span>
                <ChevronRight className="w-6 h-6 ml-1" />
              </div>
            </div>
          ))
        ) : (
          <NotFound
            small
            height={windowSize[1] - 80}
            title="Nothing here"
            subtext="Use the search bar to add instruments."
          />
        )}
      </div>
    </div>
  );
}